import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import {
  deleteRequest,
  getRequest,
  postRequest,
} from "../../utils/httpRequest";

// Comments under a blog post. Anyone can read them; signed-in members can
// write. A new member's comment waits for an admin, and until then only its
// author sees it, marked as waiting (API::BlogCommentsController).

interface BlogComment {
  id: number;
  // Built and escaped on the server (BlogComments::Format): plain text with
  // links, never the commenter's own HTML.
  html: string;
  posted_on: string;
  pending: boolean;
  can_delete: boolean;
  author: {
    name: string;
    picture_url: string;
  };
}

interface Props {
  slug: string;
  // Changes when someone signs in, so their own waiting comments show.
  currentUserKind?: string;
  handleShowSignInSideBar?: (redirectUrl?: string) => void;
}

const MAX_LENGTH = 2000;
// Where sign-in brings a reader back to. The query, not just a #comments
// anchor, so the browser really reloads the page signed in; the section then
// scrolls itself into view and tidies the address.
const BACK_PARAM = "to";
const BACK_VALUE = "comments";

const BlogComments: React.FC<Props> = ({
  slug,
  currentUserKind,
  handleShowSignInSideBar,
}) => {
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [signedIn, setSignedIn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const url = `/api/blog-posts/${slug}/comments.json`;

  useEffect(() => {
    let cancelled = false;

    getRequest(url)
      .then((response: AxiosResponse) => {
        if (cancelled) return;
        setComments(response.data.comments);
        setSignedIn(response.data.signed_in);
        setLoaded(true);
        cameBackFromSignIn();
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [url, currentUserKind]);

  const cameBackFromSignIn = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(BACK_PARAM) !== BACK_VALUE) return;

    window.history.replaceState(null, "", window.location.pathname);

    const goToComments = () => {
      document.getElementById("comments")?.scrollIntoView({ block: "start" });
      document
        .getElementById("blog-comment-content")
        ?.focus({ preventScroll: true });
    };
    // The post's pictures are often still loading and push the section down
    // as they arrive, so go there now and again once those above it have
    // loaded. (Lazy pictures below it may never load until scrolled to.)
    const section = document.getElementById("comments");
    const pending = Array.from(
      document.querySelectorAll<HTMLImageElement>(".blog-post-page img"),
    ).filter(
      (img) =>
        !img.complete &&
        section &&
        img.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
    window.setTimeout(goToComments, 0);
    if (pending.length > 0) {
      let left = pending.length;
      const settle = () => {
        left -= 1;
        if (left === 0) goToComments();
      };
      pending.forEach((img) => {
        img.addEventListener("load", settle, { once: true });
        img.addEventListener("error", settle, { once: true });
      });
    }
  };

  // The server sends every kind of sign-in (email, LinkedIn, Google, a new
  // account after its welcome step) back to this address. Passing it to the
  // panel instead would label it "Step 1 of 2", which is checkout's wording.
  const signIn = () => {
    const back = `${window.location.pathname}?${BACK_PARAM}=${BACK_VALUE}`;
    postRequest("/api/set_enroller_path", { enroller_path: back })
      .catch(() => null)
      .then(() => handleShowSignInSideBar?.());
  };

  const submit = (e) => {
    e.preventDefault();
    if (!content.trim()) {
      setError("Write something first.");
      return;
    }

    setSubmitting(true);
    setError("");
    setNotice("");

    postRequest(url, { comment: { content } })
      .then((response: AxiosResponse) => {
        const comment: BlogComment = response.data.comment;
        setComments((previous) =>
          previous.some((c) => c.id === comment.id)
            ? previous
            : [...previous, comment],
        );
        setContent("");
        if (comment.pending) {
          setNotice(
            "Thanks! Comments from new members are checked before they appear. Yours is above, and only you can see it until it's approved.",
          );
        }
      })
      .catch((err) => {
        setError(
          err?.response?.data?.error ||
            "That didn't post. Please try again in a moment.",
        );
      })
      .then(() => setSubmitting(false));
  };

  const remove = (comment: BlogComment) => {
    if (!window.confirm("Delete this comment?")) return;

    deleteRequest(`/api/blog-posts/${slug}/comments/${comment.id}.json`)
      .then(() =>
        setComments((previous) => previous.filter((c) => c.id !== comment.id)),
      )
      .catch((err) => {
        setError(
          err?.response?.data?.error || "That didn't delete. Please try again.",
        );
      });
  };

  const publishedCount = comments.filter((c) => !c.pending).length;

  return (
    <section
      className="blog-comments"
      id="comments"
      aria-labelledby="blog-comments-title"
    >
      <h2 id="blog-comments-title">
        Comments
        {publishedCount > 0 && (
          <span className="blog-comments__count">{publishedCount}</span>
        )}
      </h2>

      {loaded && comments.length === 0 && (
        <p className="blog-comments__empty">
          No comments yet. Start the conversation.
        </p>
      )}

      {comments.length > 0 && (
        <ol className="blog-comments__list">
          {comments.map((comment) => (
            <li
              key={comment.id}
              className={`blog-comments__item${
                comment.pending ? " is-pending" : ""
              }`}
            >
              <img
                className="blog-comments__avatar"
                src={comment.author.picture_url}
                alt=""
                loading="lazy"
              />
              <div className="blog-comments__main">
                <div className="blog-comments__meta">
                  <strong>{comment.author.name}</strong>
                  <span>{comment.posted_on}</span>
                  {comment.pending && (
                    <span className="blog-comments__pending">
                      Waiting for approval · only you can see this
                    </span>
                  )}
                </div>
                <div
                  className="blog-comments__text"
                  dangerouslySetInnerHTML={{ __html: comment.html }}
                />
                {comment.can_delete && (
                  <button
                    type="button"
                    className="blog-comments__delete"
                    onClick={() => remove(comment)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {loaded && signedIn && (
        <form className="blog-comments__form" onSubmit={submit}>
          <label htmlFor="blog-comment-content">Add a comment</label>
          <textarea
            id="blog-comment-content"
            rows={4}
            maxLength={MAX_LENGTH}
            placeholder="What did you think?"
            value={content}
            onChange={(e) => {
              setError("");
              setContent(e.target.value);
            }}
          />
          <div className="blog-comments__actions">
            <span className="blog-comments__hint">
              Be kind and stay on topic. Comments from new members are checked
              before they appear.
            </span>
            <button
              type="submit"
              className={`btn btn-blue btn--compact${
                submitting ? " loading" : ""
              }`}
              disabled={submitting}
            >
              Post comment
            </button>
          </div>
          {error && (
            <p className="blog-comments__error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="blog-comments__notice" role="status">
              {notice}
            </p>
          )}
        </form>
      )}

      {loaded && !signedIn && (
        <div className="blog-comments__signin">
          <p>Sign in to join the conversation. It's free.</p>
          <button
            type="button"
            className="btn btn-blue btn--compact"
            onClick={signIn}
          >
            Sign in to comment
          </button>
        </div>
      )}
    </section>
  );
};

export default BlogComments;
